import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, Play, Volume2, VolumeX, LogIn } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { FullScreenLoader } from "../components/ui-kit";

const isVideoUrl = (u = "") => /\.(mp4|webm|mov)(\?|$)/i.test(u) || u.includes("youtube.com") || u.includes("youtu.be");

const YouTubeEmbed = ({ url }) => {
  let id = "";
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes("youtu.be")) id = parsed.pathname.slice(1);
    else id = parsed.searchParams.get("v") || parsed.pathname.split("/").pop();
  } catch { id = ""; }
  if (!id) return null;
  return (
    <iframe
      title="yt"
      src={`https://www.youtube.com/embed/${id}?autoplay=1&mute=1&controls=0&loop=1&playlist=${id}&modestbranding=1`}
      className="absolute inset-0 w-full h-full pointer-events-none"
      allow="autoplay; encrypted-media"
      frameBorder="0"
    />
  );
};

const ReelCard = ({ item, muted, onToggleMute }) => {
  const videoRef = useRef(null);
  const isVideo = item.type === "video" && item.media_url;
  const isYT = isVideo && (item.media_url.includes("youtube.com") || item.media_url.includes("youtu.be"));

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, []);

  return (
    <div
      data-testid={`reel-card-${item.id}`}
      className="h-[calc(100dvh-140px)] w-full snap-start relative bg-black flex-shrink-0 overflow-hidden rounded-3xl mb-4"
    >
      {isVideo && isYT ? (
        <YouTubeEmbed url={item.media_url} />
      ) : isVideo ? (
        <video
          ref={videoRef}
          src={item.media_url}
          poster={item.thumbnail_url}
          className="absolute inset-0 w-full h-full object-cover"
          muted={muted}
          loop
          playsInline
          autoPlay
        />
      ) : (
        <img
          src={item.thumbnail_url || "https://images.unsplash.com/photo-1522075782449-e45a34f1ddfb"}
          alt={item.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/85" />

      {isVideo && !isYT && (
        <button
          data-testid={`reel-mute-${item.id}`}
          onClick={onToggleMute}
          className="absolute top-5 right-5 w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white z-10"
        >
          {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>
      )}

      <div className="absolute inset-x-0 bottom-0 p-6 pb-8 text-white">
        <div className="inline-block px-3 py-1 rounded-full bg-[#F4844B]/90 text-xs font-medium tracking-wide mb-3 uppercase">
          {item.topic}
        </div>
        <h2 className="font-instrument text-2xl font-semibold leading-tight mb-2">{item.title}</h2>
        {item.body && <p className="text-sm text-white/85 leading-relaxed line-clamp-4">{item.body}</p>}
      </div>
    </div>
  );
};

const Awareness = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTopic = searchParams.get("topic") || "";
  const [topics, setTopics] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [muted, setMuted] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/public/awareness/topics").then((r) => setTopics(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 50 };
    if (activeTopic) params.topic = activeTopic;
    api.get("/public/awareness", { params })
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [activeTopic]);

  const setTopic = (t) => {
    if (t) setSearchParams({ topic: t });
    else setSearchParams({});
  };

  return (
    <div className="min-h-[100dvh] bg-[#FFF7EE] pb-24 relative">
      <div className="sticky top-0 z-30 bg-[#FFF7EE]/95 backdrop-blur-xl px-5 pt-5 pb-3 border-b border-[#0A4C36]/5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {user ? (
              <button onClick={() => navigate("/dashboard")} data-testid="awareness-back" className="w-9 h-9 rounded-full bg-white flex items-center justify-center border border-[#0A4C36]/10">
                <ChevronLeft size={20} className="text-[#0A4C36]" />
              </button>
            ) : null}
            <div>
              <h1 className="font-instrument text-2xl font-semibold text-[#0A4C36] leading-tight">Awareness</h1>
              <p className="text-xs text-[#78716C]">Health tips crafted by clinicians</p>
            </div>
          </div>
          {!user && (
            <Link to="/login" data-testid="awareness-login-cta"
              className="flex items-center gap-1.5 text-xs font-medium text-[#0A4C36] bg-[#E6F0EC] px-3 py-2 rounded-full">
              <LogIn size={14} /> Sign in
            </Link>
          )}
        </div>
        <div className="flex gap-2 overflow-x-auto hide-scrollbar -mx-5 px-5">
          <button
            data-testid="topic-chip-all"
            onClick={() => setTopic("")}
            className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              !activeTopic ? "bg-[#0A4C36] text-white" : "bg-white text-[#57534E] border border-[#0A4C36]/10"
            }`}
          >All</button>
          {topics.map((t) => (
            <button
              key={t.topic}
              data-testid={`topic-chip-${t.topic}`}
              onClick={() => setTopic(t.topic)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                activeTopic === t.topic ? "bg-[#0A4C36] text-white" : "bg-white text-[#57534E] border border-[#0A4C36]/10"
              }`}
            >
              {t.topic} <span className="opacity-60">· {t.count}</span>
            </button>
          ))}
        </div>
      </div>

      {loading ? <FullScreenLoader /> : items.length === 0 ? (
        <div className="text-center py-20 px-6 text-[#78716C]">
          <p className="text-sm">No awareness content yet for this topic.</p>
        </div>
      ) : (
        <div className="px-4 pt-4 snap-y snap-mandatory overflow-y-scroll hide-scrollbar" style={{ maxHeight: "calc(100dvh - 120px)" }}>
          {items.map((it) => (
            <ReelCard key={it.id} item={it} muted={muted} onToggleMute={() => setMuted((m) => !m)} />
          ))}
          {!user && (
            <div className="card mb-24 text-center">
              <div className="inline-block w-12 h-12 rounded-2xl bg-[#FCD9C8] flex items-center justify-center mb-3">
                <Play size={20} className="text-[#F4844B]" />
              </div>
              <h3 className="font-instrument text-lg font-semibold text-[#0A4C36]">Join Health Circles</h3>
              <p className="text-sm text-[#57534E] mt-1 mb-4">
                Sign up to book appointments, track vitals, and get personalised care.
              </p>
              <Link to="/signup" data-testid="awareness-signup-cta" className="btn-primary inline-block px-8 py-3 text-center no-underline">
                Get started
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Awareness;
